import { useDictation } from '@formant/chat';
import { useEffect, useRef, type TouchEvent } from 'react';

import './hold-to-record-button.css';

// Below this hold duration, a press is treated as an accidental tap (finger
// slipped, misclick) and the recording is discarded instead of transcribed.
const minHoldMs = 300;
// How long a touch event suppresses the synthetic mouse events that follow
// it on most mobile browsers (touchstart/touchend fire a trailing
// mousedown/mouseup/click a few hundred ms later).
const touchGhostMs = 500;

const iconSize = 16;
const iconCommon = {
  width: iconSize,
  height: iconSize,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function MicIcon() {
  return (
    <svg {...iconCommon}>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
}

function StopSquareIcon() {
  return (
    <svg {...iconCommon} fill="currentColor" stroke="none">
      <rect x="6" y="6" width="12" height="12" rx="1.5" />
    </svg>
  );
}

/**
 * Press-and-hold mic button: mousedown/touchstart starts recording,
 * mouseup/touchend/mouseleave/touchcancel stops it and transcribes via the
 * shared `useDictation` hook (which POSTs to theopolis's /transcribe). Holds
 * shorter than `minHoldMs` are discarded rather than transcribed.
 */
export function HoldToRecordButton({
  onText,
  disabled,
  className,
  title,
}: {
  /** Called with the trimmed transcript once transcription succeeds. */
  onText: (text: string) => void;
  disabled?: boolean;
  /** Extra class(es) for surface-specific sizing/placement. */
  className?: string;
  /** Idle tooltip/aria-label. Defaults to "Hold to record". */
  title?: string;
}) {
  const dictation = useDictation(onText);
  const heldRef = useRef(false);
  const pressStartRef = useRef<number | null>(null);
  const suppressMouseUntilRef = useRef(0);
  // Set on release if `getUserMedia` hasn't resolved into 'recording' yet
  // (permission prompt, slow start) — the effect below finishes the job the
  // instant recording actually begins, instead of stop/cancel silently
  // no-op'ing against a hook that's still 'idle'.
  const pendingReleaseElapsedRef = useRef<number | null>(null);

  function beginHold() {
    if (disabled) return;
    heldRef.current = true;
    pressStartRef.current = Date.now();
    pendingReleaseElapsedRef.current = null;
    dictation.start();
  }

  function endHold() {
    if (!heldRef.current) return;
    heldRef.current = false;
    const elapsed = pressStartRef.current !== null ? Date.now() - pressStartRef.current : minHoldMs;
    pressStartRef.current = null;
    if (dictation.state === 'recording') {
      if (elapsed < minHoldMs) dictation.cancel();
      else dictation.stop();
    } else if (dictation.state === 'idle') {
      pendingReleaseElapsedRef.current = elapsed;
    }
  }

  // Finish a release that happened before `start()` resolved.
  useEffect(() => {
    if (dictation.state !== 'recording' || pendingReleaseElapsedRef.current === null) return;
    const elapsed = pendingReleaseElapsedRef.current;
    pendingReleaseElapsedRef.current = null;
    if (elapsed < minHoldMs) dictation.cancel();
    else dictation.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictation.state]);

  function onTouchStart(e: TouchEvent<HTMLButtonElement>) {
    suppressMouseUntilRef.current = Date.now() + touchGhostMs;
    if (e.cancelable) e.preventDefault();
    beginHold();
  }

  function onTouchEnd(e: TouchEvent<HTMLButtonElement>) {
    suppressMouseUntilRef.current = Date.now() + touchGhostMs;
    if (e.cancelable) e.preventDefault();
    endHold();
  }

  function onMouseDown() {
    if (Date.now() < suppressMouseUntilRef.current) return;
    beginHold();
  }

  function onMouseUp() {
    if (Date.now() < suppressMouseUntilRef.current) return;
    endHold();
  }

  const recording = dictation.state === 'recording';
  const idleTitle = title ?? 'Hold to record';

  return (
    <div className="hold-record-btn-wrap">
      <button
        type="button"
        className={`btn btn--outline btn--icon-sm hold-record-btn is-${dictation.state} ${className ?? ''}`}
        aria-label={recording ? 'Recording — release to send' : idleTitle}
        title={recording ? 'Release to stop' : idleTitle}
        disabled={disabled || dictation.state === 'transcribing'}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onContextMenu={(e) => e.preventDefault()}
      >
        {recording ? <StopSquareIcon /> : <MicIcon />}
      </button>
      {dictation.error && (
        <div className="hold-record-btn-error" role="alert">
          {dictation.error}
        </div>
      )}
    </div>
  );
}
